import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {useRecoilState} from 'recoil';
import {debounce} from 'lodash';

import {
  scrollPositionsState,
  ScrollPositions,
  ScrollPosition,
} from '@/store/atoms';

import useScrollContext from './useScrollContext';

// 상수 정의
const SCROLL_THRESHOLD = 10; // 스크롤 위치 저장 임계값
const SCROLL_ERROR_MARGIN = 5; // 스크롤 복원 오차 허용 범위
const MAX_RETRY_COUNT = 3; // 최대 재시도 횟수
const RETRY_DELAY = 100; // 재시도 간격
const SCROLL_DEBOUNCE_DELAY = 100; // 스크롤 이벤트 디바운스 지연 시간

export const useScroll = () => {
  const location = useLocation();
  const {scrollRef} = useScrollContext();
  const [scrollPosList, setScrollPosList] =
    useRecoilState<ScrollPositions>(scrollPositionsState);

  // refs
  const mountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const isRestoringRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 컴포넌트 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // 스크롤 위치가 제대로 복원되었는지 확인
  const isScrollRestored = useCallback(
    (target: HTMLElement | Window, expectedY: number) => {
      const currentY =
        target === window ? window.scrollY : (target as HTMLElement).scrollTop;
      return Math.abs(currentY - expectedY) < SCROLL_ERROR_MARGIN;
    },
    [],
  );

  // 스크롤 복원 시도
  const attemptScrollRestore = useCallback(
    (target: HTMLElement | Window, y: number) => {
      try {
        if (target === window) {
          window.scrollTo({top: y, behavior: 'auto'});
        } else if (target === scrollRef.current) {
          (target as HTMLElement).scrollTop = y;
        }
      } catch (error) {
        console.error('Failed to restore scroll position:', error);
      }
    },
    [scrollRef],
  );

  // 스크롤 위치 저장
  const saveScrollPosition = useCallback(() => {
    if (!mountedRef.current) return;

    const target = scrollRef.current || window;
    const currentPath = `${location.pathname}${location.search}`;

    // 브랜드 리스트에서만 스크롤 위치 저장
    if (currentPath.includes('/brand/list')) {
      const y =
        target === window ? window.scrollY : (target as HTMLElement).scrollTop;

      // 스크롤 위치가 크게 변하지 않았다면 저장하지 않음
      if (Math.abs(y - lastScrollYRef.current) < SCROLL_THRESHOLD) return;

      lastScrollYRef.current = y;

      const newPosition: ScrollPosition = {
        x: 0,
        y,
        clickedItemId: undefined,
        clickedItemIndex: undefined,
        timestamp: Date.now(),
      };
      setScrollPosList(prev => ({
        ...prev,
        [currentPath]: newPosition,
      }));
    }
  }, [location.pathname, location.search]);

  // 스크롤 복원
  const syncScroll = useCallback(() => {
    if (!mountedRef.current || isRestoringRef.current) return;

    const target = scrollRef.current || window;
    const currentPath = `${location.pathname}${location.search}`;
    const sp = scrollPosList[currentPath] as ScrollPosition;

    // 브랜드 리스트에서만 스크롤 복원
    if (currentPath.includes('/brand/list') && sp?.y) {
      isRestoringRef.current = true;
      retryCountRef.current = 0;

      const restoreScroll = () => {
        if (!mountedRef.current) {
          isRestoringRef.current = false;
          return;
        }

        attemptScrollRestore(target, sp.y);

        if (
          !isScrollRestored(target, sp.y) &&
          retryCountRef.current < MAX_RETRY_COUNT
        ) {
          retryCountRef.current += 1;
          timeoutRef.current = setTimeout(restoreScroll, RETRY_DELAY);
        } else {
          isRestoringRef.current = false;
          retryCountRef.current = 0;
        }
      };

      // 초기 스크롤 복원 시도
      timeoutRef.current = setTimeout(restoreScroll, 0);
    } else {
      // 상세 페이지 진입 시 스크롤 초기화
      if (target === window) {
        window.scrollTo(0, 0);
      } else if (target === scrollRef.current) {
        (target as HTMLElement).scrollTop = 0;
      }
    }
  }, [
    location.pathname,
    location.search,
    scrollPosList,
    attemptScrollRestore,
    isScrollRestored,
  ]);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    if (!mountedRef.current) return;

    const target = scrollRef.current || window;
    const debouncedSave = debounce(saveScrollPosition, SCROLL_DEBOUNCE_DELAY);

    // 브랜드 리스트에서만 스크롤 이벤트 리스너 등록
    if (location.pathname.includes('/brand/list')) {
      target.addEventListener('scroll', debouncedSave);
      return () => {
        target.removeEventListener('scroll', debouncedSave);
        debouncedSave.cancel();
      };
    }
  }, [saveScrollPosition, location.pathname]);

  // 페이지 진입 시 스크롤 복원
  useEffect(() => {
    if (!mountedRef.current) return;

    const currentPath = `${location.pathname}${location.search}`;
    if (!currentPath.includes('/brand/list')) return;

    const sp = scrollPosList[currentPath] as ScrollPosition;
    if (!sp?.y) return;

    const target = scrollRef.current || window;

    // MutationObserver 설정
    observerRef.current = new MutationObserver(() => {
      if (!mountedRef.current) return;

      const currentY =
        target === window ? window.scrollY : (target as HTMLElement).scrollTop;

      // 스크롤이 복원되지 않았고, 현재 위치가 0에 가깝다면 복원 시도
      if (!isScrollRestored(target, sp.y) && currentY < SCROLL_THRESHOLD) {
        syncScroll();
      }
    });

    // 관찰 대상 설정
    const targetNode = document.body;
    const config = {
      childList: true,
      subtree: true,
      attributes: true,
    };

    observerRef.current.observe(targetNode, config);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    location.pathname,
    location.search,
    scrollPosList,
    isScrollRestored,
    syncScroll,
  ]);

  // 스크롤 위치 초기화
  const resetScroll = useCallback(
    (path?: string) => {
      const target = scrollRef.current || window;
      const currentPath = path || `${location.pathname}${location.search}`;

      // 스크롤 위치 초기화
      if (target === window) {
        window.scrollTo(0, 0);
      } else if (target === scrollRef.current) {
        (target as HTMLElement).scrollTop = 0;
      }

      // 해당 경로의 스크롤 위치 정보도 초기화
      setScrollPosList(prev => {
        const newPositions = {...prev};
        delete newPositions[currentPath];
        return newPositions;
      });
    },
    [scrollRef, location.pathname, location.search, setScrollPosList],
  );

  // 스크롤 위치 확인
  const checkScrolled = useCallback(
    (path?: string) => {
      const target = scrollRef.current || window;
      const currentPath = path || `${location.pathname}${location.search}`;
      const sp = scrollPosList[currentPath] as ScrollPosition;

      // 저장된 스크롤 위치가 있으면 해당 위치와 비교
      if (sp?.y !== undefined) {
        const currentY =
          target === window
            ? window.scrollY
            : (target as HTMLElement).scrollTop;
        return Math.abs(currentY - sp.y) > SCROLL_ERROR_MARGIN;
      }

      // 저장된 위치가 없으면 현재 스크롤 위치 확인
      return target === window
        ? window.scrollY > 0
        : (target as HTMLElement).scrollTop > 0;
    },
    [scrollRef, location.pathname, location.search, scrollPosList],
  );

  // 스크롤 위치 존재 여부 확인
  const hasScrollPosition = useCallback(
    (path: string) => {
      return !!scrollPosList[path]?.y;
    },
    [scrollPosList],
  );

  // 스크롤 ref 설정
  const setCombinedRef = useCallback(
    (
      element: HTMLElement | null,
      externalRef?: React.MutableRefObject<HTMLElement | null>,
    ) => {
      if (element) {
        scrollRef.current = element;
        if (externalRef) {
          externalRef.current = element;
        }
      }
    },
    [scrollRef],
  );

  return {
    syncScroll,
    saveScrollPosition,
    resetScroll,
    checkScrolled,
    hasScrollPosition,
    setCombinedRef,
  };
};
